import React, { useState } from 'react'
import axios from 'axios'
import { Box, TextField, Button, Typography, Alert, Link, CircularProgress } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EmailIcon from '@mui/icons-material/Email'
import useRouter from '../hooks/useRouter'
import useSnackbar from '../hooks/useSnackbar'
import { extractErrorMessage } from '../utils/apitools'

// The server answers a reset request the same whether or not the address has
// an account, so the page never says an email was sent.
const RESET_ACK = "If that address has an account, we've emailed a reset link."

export const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [ack, setAck] = useState(RESET_ACK)
  const [error, setError] = useState('')
  
  const snackbar = useSnackbar()
  const router = useRouter()

  const handleSubmit = async () => {
    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    try {
      setLoading(true)
      setError('')
      
      // Call the plea endpoint with the email
      const httpResponse: any = await axios.post('/api/v1/user/password/plea', { email })
      const response = httpResponse.data
      
      // Check if response has error
      if (response.hasError) {
        setError(response.message || 'Failed to send reset email')
        return
      }
      
      const message = typeof response.message === 'string' && response.message ? response.message : RESET_ACK
      setAck(message)
      setSuccess(true)
      snackbar.success(message)
    } catch (err: any) {
      const message = extractErrorMessage(err)
      setError(message)
      snackbar.error(message)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    router.navigate('home')
  }

  if (success) {
    return (
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
        {/* Left section - hidden on small screens */}
        <Box sx={{ width: '50%', bgcolor: 'white', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
          <img
            src="/img/platinum-banner.gif"
            alt="Platinum Title Banner"
            style={{ width: '100%', maxWidth: '100%', height: 'auto' }}
          />
        </Box>

        {/* Right section - full width on small screens */}
        <Box sx={{ width: { xs: '100%', sm: '50%' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box
            sx={{
              maxWidth: 400,
              width: '100%',
              p: 3,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center'
            }}
          >
            <EmailIcon sx={{ fontSize: 64, color: 'info.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Check Your Email
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              {ack}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Click the link in the email to reset your password. If you don't see it, check your spam folder.
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              color="info"
              onClick={handleBackToLogin}
              sx={{
                minHeight: '40px',
                fontSize: '0.875rem',
              }}
            >
              Back to Login
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Left section */}
      <Box sx={{ width: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <img 
          src="/img/platinum-banner.gif" 
          alt="Platinum Title Banner" 
          style={{ width: '100%', maxWidth: '100%', height: 'auto' }} 
        />
      </Box>

      {/* Right section */}
      <Box sx={{ width: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          sx={{
            maxWidth: 400,
            width: '100%',
            p: 3,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={handleBackToLogin}
              sx={{
                color: 'info.main',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              <ArrowBackIcon fontSize="small" sx={{ mr: 0.5 }} />
              Back to Login
            </Link>
          </Box>
          
          <Typography variant="h4" component="h1" gutterBottom>
            Reset Password
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              void handleSubmit()
            }}
          >
            <TextField
              fullWidth
              autoFocus
              autoComplete="email"
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              variant="outlined"
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                sx: {
                  height: '40px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) => theme.palette.info.main + '80', // 50% opacity
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'info.main',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'info.main',
                  },
                },
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="info"
              disabled={loading}
              sx={{
                mt: 2,
                minHeight: '40px',
                fontSize: '0.875rem',
              }}
            >
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'info.contrastText' }} />
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ForgotPassword 
